import { Textarea } from '@/components/ui/textarea';
import { notify } from '@/lib/toast';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { ArrowRightLeft, Plus, Truck, CheckCircle, AlertTriangle } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { ProductSearchCombobox } from '@/components/inventory/ProductSearchCombobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { SearchInput } from '@/components/ui/search-input';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { Dropdown } from 'primereact/dropdown';


/** List endpoints return either a bare array or `{ data }`. */
type ListResponse<T> = T[] | { data?: T[] };

function unwrapList<T>(res: ListResponse<T> | undefined): T[] {
  return Array.isArray(res) ? res : (res?.data ?? []);
}

interface LocationRow {
  id: string;
  name: string;
}

interface TransferProduct {
  id: string;
  name: string;
  sku?: string | null;
  stockQuantity?: number | null;
}

interface TransferItem {
  id: string;
  productId: string;
  requestedQty: number;
  sentQty: number;
  receivedQty: number;
  product?: { id: string; name: string; sku?: string | null } | null;
}

interface StockTransfer {
  id: string;
  transferNumber: string;
  status: string;
  fromLocationId: string;
  toLocationId: string;
  notes?: string | null;
  createdAt: string;
  items?: TransferItem[];
}

interface TransferFormItem {
  productId: string;
  requestedQty: number;
  /** Picker selection (UI-only; stripped before submit). */
  _product: TransferProduct | null;
}

interface TransferForm {
  fromLocationId: string;
  toLocationId: string;
  notes: string;
  items: TransferFormItem[];
}

interface CreateTransferBody {
  fromLocationId: string;
  toLocationId: string;
  notes: string;
  items: { productId: string; requestedQty: number }[];
}

const emptyItem: TransferFormItem = { productId: '', requestedQty: 1, _product: null };
const emptyForm: TransferForm = { fromLocationId: '', toLocationId: '', notes: '', items: [{ ...emptyItem }] };

export function StockTransfersPage() {
  const { t } = useTranslation('inventory');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailTransfer, setDetailTransfer] = useState<StockTransfer | null>(null);
  const [receiveQtys, setReceiveQtys] = useState<Record<string, number>>({});
  const [varianceReason, setVarianceReason] = useState('');
  const [form, setForm] = useState<TransferForm>(emptyForm);
  const [cancelConfirm, setCancelConfirm] = useState<{ open: boolean; id: string; inTransit: boolean }>({ open: false, id: '', inTransit: false });
  // Per-row autocomplete suggestions (indexed by item position)

  const queryClient = useQueryClient();
  const handleTransferUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
  }, [queryClient]);
  useSocketEvent('stock.updated', handleTransferUpdate);
  useSocketEvent('transfer.updated', handleTransferUpdate);
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('transfers:create') || hasPermission('transfers:*');
  const canUpdate = hasPermission('transfers:update') || hasPermission('transfers:*');

  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const res = await api.get<ListResponse<LocationRow>>('/locations');
      return res.data;
    },
  });
  const locations: LocationRow[] = unwrapList(locationsData);

  // POS-I260: the item picker searches the server (ProductSearchCombobox); only stock-tracked products
  // at the source location can be transferred. Names on existing transfers come from the transfer items.
  const transferProductFilters = {
    trackStock: 'true',
    ...(form.fromLocationId ? { locationId: form.fromLocationId } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['stock-transfers', search, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get<ListResponse<StockTransfer>>(`/stock-transfers?${params}`);
      return res.data;
    },
  });
  const transfers: StockTransfer[] = unwrapList(data);

  const createMutation = useMutation({
    mutationFn: (body: CreateTransferBody) => api.post('/stock-transfers', body),
    onSuccess: () => {
      toast.success('Transfer created');
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      setCreateOpen(false);
      setForm(emptyForm);
    },
    onError: (err) => notify.error(err, 'Failed to create transfer'),
  });

  const shipMutation = useMutation({
    mutationFn: ({ id, items }: { id: string; items: { transferItemId: string; sentQty: number }[] }) =>
      api.post(`/stock-transfers/${id}/ship`, { items }),
    onSuccess: () => {
      toast.success('Transfer shipped — now in transit');
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['location-stock'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      setDetailTransfer(null);
    },
    onError: (err) => notify.error(err, 'Ship failed'),
  });

  const receiveMutation = useMutation({
    mutationFn: ({ id, items, varianceReason }: { id: string; items: { itemId: string; receivedQty: number }[]; varianceReason?: string }) =>
      api.post(`/stock-transfers/${id}/receive`, { items, varianceReason }),
    onSuccess: () => {
      toast.success('Transfer received — completed!');
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['location-stock'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      setDetailTransfer(null);
      setVarianceReason('');
    },
    onError: (err) => notify.error(err, 'Receive failed'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.post(`/stock-transfers/${id}/cancel`),
    onSuccess: () => {
      toast.success('Transfer cancelled — stock restored to source location');
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['location-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements-full'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      setDetailTransfer(null);
      setCancelConfirm({ open: false, id: '', inTransit: false });
    },
    onError: (err) => notify.error(err, 'Cancel failed'),
  });

  function updateItem<K extends keyof TransferFormItem>(index: number, field: K, value: TransferFormItem[K]) {
    const items = [...form.items];
    items[index] = { ...items[index], [field]: value };
    setForm({ ...form, items });
  }

  function openDetail(transfer: StockTransfer) {
    setDetailTransfer(transfer);
    const qtys: Record<string, number> = {};
    (transfer.items ?? []).forEach((item) => { qtys[item.id] = item.sentQty || item.requestedQty; });
    setReceiveQtys(qtys);
    setVarianceReason('');
  }

  function getLocationName(id: string) {
    return locations.find((l) => l.id === id)?.name ?? id;
  }

  function getProductName(item: TransferItem) {
    return item.product?.name ?? item.productId;
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Stock Transfers"
        subtitle="Transfer inventory between locations and warehouses"
        actions={canCreate && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 me-2" /> New Transfer
          </Button>
        )}
      />

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search transfers..."
          className="flex-1 min-w-[200px]"
        />
        <Dropdown
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.value)}
          options={[
            { label: 'All Statuses', value: '' },
            { label: 'Draft', value: 'DRAFT' },
            { label: 'In Transit', value: 'IN_TRANSIT' },
            { label: 'Completed', value: 'COMPLETED' },
            { label: 'Cancelled', value: 'CANCELLED' },
          ]}
          optionLabel="label"
          optionValue="value"
          placeholder="All Statuses"
        />
      </div>

      {/* Transfers List */}
      {isLoading ? (
        <SkeletonTable rows={4} cols={3} />
      ) : transfers.length === 0 ? (
        <EmptyState
          icon={ArrowRightLeft}
          title="No stock transfers yet"
          subtitle="Create a transfer to move inventory between locations."
          action={canCreate ? { label: 'Create Transfer', onClick: () => setCreateOpen(true) } : undefined}
        />
      ) : (
        <div className="space-y-3">
          {transfers.map((t) => (
            <Card key={t.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDetail(t)}>
              <CardContent className="flex items-center justify-between py-4 px-5">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium text-sm">{t.transferNumber}</span>
                    <StatusBadge status={t.status} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getLocationName(t.fromLocationId)} → {getLocationName(t.toLocationId)}
                  </p>
                  {t.notes && <p className="text-xs text-muted-foreground italic">{t.notes}</p>}
                </div>
                <div className="text-end text-sm">
                  <p className="text-muted-foreground">{(t.items ?? []).length} item(s)</p>
                  <p className="text-xs text-muted-foreground">{new Date(t.createdAt).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Transfer Dialog */}
      <Dialog
        header="New Stock Transfer"
        visible={createOpen}
        onHide={() => { setCreateOpen(false); setForm(emptyForm); }}
        style={{ width: '900px' }}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        maximizable
        modal
      >
        <div className="space-y-4 p-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>From Location *</Label>
              <Dropdown
                value={form.fromLocationId}
                onChange={(e) => setForm({ ...form, fromLocationId: e.value })}
                options={[{ label: 'Select source...', value: '' }, ...locations.map((l) => ({ label: l.name, value: l.id }))]}
                optionLabel="label"
                optionValue="value"
                placeholder="Select source..."
                className="w-full mt-1"
              />
            </div>
            <div>
              <Label>To Location *</Label>
              <Dropdown
                value={form.toLocationId}
                onChange={(e) => setForm({ ...form, toLocationId: e.value })}
                options={[{ label: 'Select destination...', value: '' }, ...locations.filter((l) => l.id !== form.fromLocationId).map((l) => ({ label: l.name, value: l.id }))]}
                optionLabel="label"
                optionValue="value"
                placeholder="Select destination..."
                className="w-full mt-1"
              />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional notes..."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Items *</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem }] })}
              >
                + Add Item
              </Button>
            </div>
            <div className="space-y-2">
              {form.items.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <div className="flex-1 min-w-0">
                    <ProductSearchCombobox<TransferProduct>
                      value={item.productId}
                      selected={item._product}
                      filters={transferProductFilters}
                      onChange={(id, product) => {
                        const updated = form.items.map((it, i) =>
                          i === idx ? { ...it, _product: product, productId: id } : it
                        );
                        setForm({ ...form, items: updated });
                      }}
                      placeholder="Search product by name or SKU…"
                    />
                  </div>
                  <Input
                    type="number"
                    min="1"
                    value={item.requestedQty}
                    onChange={(e) => updateItem(idx, 'requestedQty', Number(e.target.value))}
                    className="w-20 px-2 text-end"
                  />
                  {form.items.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive-text px-2"
                      onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}
                    >
                      ×
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (form.fromLocationId === form.toLocationId) {
                  toast.error('Source and destination locations must be different');
                  return;
                }
                if (!form.items || form.items.length === 0) {
                  toast.error('Add at least one item to transfer');
                  return;
                }
                createMutation.mutate({
                  ...form,
                  items: form.items.map(({ _product, ...rest }) => rest),
                });
              }}
              disabled={!form.fromLocationId || !form.toLocationId || form.items.some((i) => !i.productId) || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Transfer'}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Detail / Action Dialog */}
      {detailTransfer && (
        <Dialog
          header={`Transfer ${detailTransfer.transferNumber}`}
          visible={!!detailTransfer}
          onHide={() => setDetailTransfer(null)}
          style={{ width: '900px' }}
          contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
          maximizable
          modal
        >
          <div className="space-y-4 p-1">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">From</p>
                <p className="font-medium">{getLocationName(detailTransfer.fromLocationId)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">To</p>
                <p className="font-medium">{getLocationName(detailTransfer.toLocationId)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <StatusBadge status={detailTransfer.status} />
              </div>
              {detailTransfer.notes && (
                <div>
                  <p className="text-muted-foreground">Notes</p>
                  <p>{detailTransfer.notes}</p>
                </div>
              )}
            </div>

            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('tblProduct')}</Th>
                  <Th className="text-end">{t('tblRequested')}</Th>
                  <Th className="text-end">{t('tblSent')}</Th>
                  <Th className="text-end">{t('tblReceived')}</Th>
                  {detailTransfer.status === 'IN_TRANSIT' && canUpdate && (
                    <Th className="text-end">{t('tblReceiveQty')}</Th>
                  )}
                </Tr>
              </THead>
              <TBody>
                {(detailTransfer.items ?? []).map((item) => (
                  <Tr key={item.id}>
                    <Td label={t('tblProduct')}>{getProductName(item)}</Td>
                    <Td label={t('tblRequested')} className="text-end">{item.requestedQty}</Td>
                    <Td label={t('tblSent')} className="text-end">{item.sentQty}</Td>
                    <Td label={t('tblReceived')} className="text-end">{item.receivedQty}</Td>
                    {detailTransfer.status === 'IN_TRANSIT' && canUpdate && (
                      <Td label={t('tblReceiveQty')} className="text-end">
                        <Input
                          type="number"
                          min="0"
                          max={item.sentQty}
                          value={receiveQtys[item.id] ?? item.sentQty}
                          onChange={(e) => setReceiveQtys({ ...receiveQtys, [item.id]: Number(e.target.value) })}
                          className="w-16 h-auto px-2 py-1 text-end"
                        />
                      </Td>
                    )}
                  </Tr>
                ))}
              </TBody>
            </Table>

            {detailTransfer.status === 'IN_TRANSIT' && canUpdate && (() => {
              const hasVariance = (detailTransfer.items ?? []).some(
                (i) => (receiveQtys[i.id] ?? i.sentQty) < i.sentQty,
              );
              if (!hasVariance) return null;
              return (
                <div className="mt-3 rounded-lg border border-warning/30 bg-warning/10 p-3">
                  <p className="text-sm font-medium text-warning-text flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" />
                    Shortfall detected — units short will be written off as inventory loss.
                  </p>
                  <Textarea
                    value={varianceReason}
                    onChange={(e) => setVarianceReason(e.target.value)}
                    placeholder="Reason for variance (required) — e.g. damaged in transit, miscount, theft"
                    rows={2}
                    className="mt-2"
                  />
                </div>
              );
            })()}

            <div className="flex justify-end gap-2 pt-2">
              {canUpdate && detailTransfer.status === 'DRAFT' && (
                <>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setCancelConfirm({ open: true, id: detailTransfer.id, inTransit: false })}
                  >
                    Cancel Transfer
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      const items = (detailTransfer.items ?? []).map((i) => ({
                        transferItemId: i.id,
                        sentQty: i.requestedQty,
                      }));
                      shipMutation.mutate({ id: detailTransfer.id, items });
                    }}
                    disabled={shipMutation.isPending}
                  >
                    <Truck className="h-4 w-4 me-1" />
                    {shipMutation.isPending ? 'Shipping...' : 'Ship Transfer'}
                  </Button>
                </>
              )}
              {canUpdate && detailTransfer.status === 'IN_TRANSIT' && (
                <>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setCancelConfirm({ open: true, id: detailTransfer.id, inTransit: true })}
                  >
                    Cancel & Restore Stock
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      const items = (detailTransfer.items ?? []).map((i) => ({
                        itemId: i.id,
                        receivedQty: receiveQtys[i.id] ?? i.sentQty,
                      }));
                      const hasVariance = (detailTransfer.items ?? []).some(
                        (i) => (receiveQtys[i.id] ?? i.sentQty) < i.sentQty,
                      );
                      if (hasVariance && !varianceReason.trim()) {
                        toast.error('Please provide a reason for the variance before receiving');
                        return;
                      }
                      receiveMutation.mutate({
                        id: detailTransfer.id,
                        items,
                        varianceReason: hasVariance ? varianceReason.trim() : undefined,
                      });
                    }}
                    disabled={receiveMutation.isPending}
                  >
                    <CheckCircle className="h-4 w-4 me-1" />
                    {receiveMutation.isPending ? 'Receiving...' : 'Receive Transfer'}
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={() => setDetailTransfer(null)}>Close</Button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Cancel Transfer Confirm Dialog */}
      <ConfirmDialog
        open={cancelConfirm.open}
        title="Cancel Transfer"
        message={cancelConfirm.inTransit
          ? 'Cancel this in-transit transfer? Stock will be restored to the source location.'
          : 'Cancel this transfer? This action cannot be undone.'}
        confirmLabel="Cancel Transfer"
        variant="danger"
        loading={cancelMutation.isPending}
        onConfirm={() => cancelMutation.mutate(cancelConfirm.id)}
        onCancel={() => setCancelConfirm({ open: false, id: '', inTransit: false })}
      />
    </div>
  );
}
